import { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronsLeft, ChevronsRight, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { SideUserPanel } from "@/components/layout/SideUserPanel";
import { BrandMark } from "@/components/BrandMark";
import { cn } from "@/lib/utils";
import {
  NAV_GROUPS, TOP_NAV, TOP_NAV_GROUPS,
  applyRoleFilter, applyPermissionFilter,
  activeTopNavId, firstLeafOf, visibleTopNavIds,
} from "@/components/layout/navConfig";

// Construction-site photo behind the lower part of the menu (kept as an inline style so webpack leaves the /brand path alone).
const SITE_PHOTO = { backgroundImage: "url(/brand/sidebar-site.png)", backgroundSize: "100% auto", backgroundPosition: "bottom", backgroundRepeat: "no-repeat" };

/** Vertical module list (the modules that used to be the top bar's buttons). */
export function SideNav({ collapsed, onToggleCollapse, mobileOpen, onMobileClose }) {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOrgAdmin, seesWholePortfolio } = useAuth();
  const { canView } = usePermissions();

  const visibleGroups = useMemo(
    () => applyPermissionFilter(applyRoleFilter(NAV_GROUPS, { seesWholePortfolio, isOrgAdmin }), canView),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seesWholePortfolio, isOrgAdmin, canView]
  );
  const shown = new Set(visibleTopNavIds(visibleGroups));
  const items = TOP_NAV.filter((t) => shown.has(t.id));
  const activeId = activeTopNavId(visibleGroups, location.pathname, projectId);

  // ungrouped items stay in order; grouped items collect under their group heading at first appearance
  const rows = [];
  const seen = new Set();
  items.forEach((t) => {
    if (t.group) {
      if (seen.has(t.group)) return;
      seen.add(t.group);
      rows.push({ type: "group", meta: TOP_NAV_GROUPS.find((g) => g.id === t.group), members: items.filter((m) => m.group === t.group) });
    } else rows.push({ type: "item", item: t });
  });

  const go = (id, after) => {
    const to = firstLeafOf(visibleGroups, id, projectId);
    if (to) navigate(to);
    after?.();
  };

  const Btn = ({ t, indent, narrow, after }) => {
    const Icon = t.icon;
    const active = t.id === activeId;
    return (
      <button
        type="button"
        data-testid={`topbar-item-${t.id}`}
        data-tour={`nav-${t.id}`}
        title={t.label}
        onClick={() => go(t.id, after)}
        className={cn(
          "flex w-full items-center gap-3.5 rounded-[10px] py-2.5 text-[14.5px] font-medium transition-colors",
          narrow ? "justify-center px-0" : indent ? "pl-8 pr-3.5" : "px-3.5",
          active ? "bg-[#99002B] font-semibold text-white shadow-[0_6px_16px_-6px_rgba(153,0,43,0.7)]" : "text-[#DCE3F2] hover:bg-white/[0.07]"
        )}
      >
        <Icon className={cn("h-5 w-5 shrink-0", !active && "text-[#C5CFE6]")} />
        {!narrow && <span className="truncate">{t.label}</span>}
      </button>
    );
  };

  const list = (narrow, after) => (
    <nav data-testid="sidenav-modules" style={narrow ? undefined : SITE_PHOTO} className="relative z-10 flex-1 space-y-[2px] overflow-y-auto px-1.5 py-3.5">
      {rows.map((r) =>
        r.type === "item" ? (
          <Btn key={r.item.id} t={r.item} narrow={narrow} after={after} />
        ) : (
          <div key={r.meta.id} className="pt-2">
            {!narrow && <p className="px-3.5 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#8392B2]">{r.meta.label}</p>}
            {narrow && <div className="mx-2 my-1 border-t border-white/10" />}
            {r.members.map((m) => <Btn key={m.id} t={m} narrow={narrow} indent={false} after={after} />)}
          </div>
        )
      )}
    </nav>
  );

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-black/60" onClick={onMobileClose} aria-hidden="true" />
          <aside className="relative flex h-full w-[260px] flex-col bg-[#18233C] shadow-2xl">
            <div className="flex h-[60px] shrink-0 items-center justify-between border-b border-white/10 px-4">
              <BrandMark tagline />
              <button data-testid="mobile-sidebar-close" onClick={onMobileClose} aria-label="Close navigation" className="rounded-md p-1 text-[#C5CFE6] hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            {list(false, onMobileClose)}
            <SideUserPanel collapsed={false} />
          </aside>
        </div>
      )}

      <aside
        data-testid="app-sidebar"
        data-tour="nav-sidebar"
        className={cn("relative hidden h-full shrink-0 flex-col overflow-hidden bg-[#18233C] transition-all duration-300 md:flex", collapsed ? "w-16" : "w-[230px]")}
      >
        {list(collapsed)}
        <SideUserPanel collapsed={collapsed} />
        <div className="relative z-10 shrink-0 px-2 pb-4">
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={onToggleCollapse}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn("flex h-10 items-center gap-3 rounded-lg px-3.5 text-sm text-[#C5CFE6] hover:bg-white/[0.07] hover:text-white", collapsed ? "mx-auto w-10 justify-center px-0" : "w-full")}
          >
            {collapsed ? <ChevronsRight className="h-5 w-5" /> : <><ChevronsLeft className="h-5 w-5" /> Collapse</>}
          </button>
        </div>
      </aside>
    </>
  );
}
